<template>
  <GbwapsLayout title="GB WhatsApp Features" :intro="pageIntro" :breadcrumb="breadcrumb">
    <div class="features-page">
      <div class="warning-box features-note">
        <strong>Note:</strong>
        {{ pageNote }}
      </div>

      <section id="popular-features" class="features-block page-section">
        <h2>Popular GB WhatsApp Features</h2>
        <p>
          Looking for a specific feature? Start with some of the most popular tools GB WhatsApp users look for.
        </p>
        <div class="table-scroll">
          <table class="app-info-table features-popular-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>What It Does</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in popularFeatures" :key="row.feature">
                <td>{{ row.feature }}</td>
                <td>{{ row.what }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-for="category in categories"
        :id="category.id"
        :key="category.id"
        class="features-category"
        :class="'features-category--' + category.theme">
        <h2>{{ category.h2 }}</h2>
        <p class="features-category__intro">{{ category.intro }}</p>

        <article
          v-for="item in category.items"
          :id="item.id"
          :key="item.id"
          class="features-item">
          <h3>{{ item.h3 }}</h3>
          <p v-for="(para, idx) in item.paragraphs" :key="idx">{{ para }}</p>

          <figure v-if="item.image" class="features-item__figure">
            <img
              :src="item.image.src"
              :alt="item.image.alt"
              loading="lazy"
              width="720"
              height="405">
          </figure>

          <RelatedGuideCard v-if="item.relatedGuide" :guide="item.relatedGuide" />
        </article>
      </section>

      <section id="features-vs-official" class="features-block page-section">
        <h2>GB WhatsApp Features vs Official WhatsApp</h2>
        <p>
          The biggest difference is not simply the number of features. Official WhatsApp provides a supported and
          regularly maintained messaging service, while GB WhatsApp focuses on adding customization and extra controls
          through an unofficial modified client.
        </p>
        <div class="table-scroll">
          <table class="comparison-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Official WhatsApp</th>
                <th>GB WhatsApp</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in comparisonRows" :key="row.feature">
                <td>{{ row.feature }}</td>
                <td>{{ row.official }}</td>
                <td>{{ row.gb }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          The table shows why GB WhatsApp can be attractive to users who want more control, but also why it comes with
          important trade-offs. Features can change or stop working after an update, and unofficial apps do not receive
          the same official support as WhatsApp.
        </p>
      </section>

      <section id="most-useful" class="features-block page-section">
        <h2>Which GB WhatsApp Features Are Most Useful?</h2>
        <p>
          You do not need to enable every option available in your build. For many users, a few features provide most of
          the practical benefits.
        </p>
        <ul class="features-useful-list">
          <li v-for="group in usefulGroups" :key="group.title">
            <strong>{{ group.title }}:</strong>
            {{ group.text }}
          </li>
        </ul>
      </section>

      <section id="important-things" class="features-block page-section">
        <h2>Important Things to Know Before Using GB WhatsApp</h2>
        <p v-for="(para, idx) in importantNotes" :key="idx">{{ para }}</p>
      </section>

      <section id="features-faq" class="features-block page-section">
        <h2>Frequently Asked Questions</h2>
        <FaqAccordion :items="faqItems" />
      </section>

      <div class="features-page-cta card text-center">
        <h3>Try GB WhatsApp features</h3>
        <p>Download the latest APK, then enable privacy tools one at a time.</p>
        <div class="features-page-cta-actions">
          <router-link to="/download/" class="btn-primary">Download GB WhatsApp APK</router-link>
          <router-link to="/install-gbwhatsapp/" class="btn-secondary">Install Guide</router-link>
        </div>
      </div>
    </div>
  </GbwapsLayout>
</template>

<script>
import '@/css/gbwaps/pages.css';
import GbwapsLayout from '@/layouts/GbwapsLayout.vue';
import RelatedGuideCard from '@/components/gbwaps/RelatedGuideCard.vue';
import FaqAccordion from '@/components/gbwaps/FaqAccordion.vue';
import {
	FEATURES_PAGE_INTRO,
	FEATURES_PAGE_NOTE,
	POPULAR_FEATURES,
	FEATURE_CATEGORIES,
	FEATURES_COMPARISON_ROWS,
	FEATURES_USEFUL_GROUPS,
	FEATURES_IMPORTANT,
	FEATURES_FAQ,
} from '@/data/features-content';

export default {
	name: 'FeaturesPage',
	components: { GbwapsLayout, RelatedGuideCard, FaqAccordion },
	data() {
		return {
			pageIntro: FEATURES_PAGE_INTRO,
			pageNote: FEATURES_PAGE_NOTE,
			breadcrumb: [{ name: 'Home', to: '/' }, { name: 'Features' }],
			popularFeatures: POPULAR_FEATURES,
			categories: FEATURE_CATEGORIES,
			comparisonRows: FEATURES_COMPARISON_ROWS,
			usefulGroups: FEATURES_USEFUL_GROUPS,
			importantNotes: FEATURES_IMPORTANT,
			faqItems: FEATURES_FAQ,
		};
	},
};
</script>
